import { handleActions } from 'redux-actions';
import { Actions, getCheckInsFailure, getCheckInsRequest, getCheckInsSuccess } from 'actions/checkInsActions';
import { Actions as SessionActions, logoutSuccess } from 'actions/sessionActions';
import { Actions as DeviceActions, deleteDeviceSuccess } from 'actions/deviceActions';
import { DeviceId, CheckIn } from 'shared/models';
import { omit } from 'lodash';

// Only store 300 check ins per device, which is typically just over three days of check ins
const MAX_CHECKINS = 300;

export type State = Readonly<{
    data: Record<DeviceId, CheckIn[]>;
    loading: Record<DeviceId, boolean>;
    errors: Record<DeviceId, Error>;
}>;

export const initialState = Object.freeze<State>({
    data: {},
    loading: {},
    errors: {},
});

export const checkInsReducer = handleActions({
    [Actions.GET_CHECKINS_REQUEST]: (state: State, action: ReturnType<typeof getCheckInsRequest>) => {
        const deviceId = action.payload;
        return {
            ...state,
            loading: {
                ...state.loading,
                [deviceId]: true,
            },
        };
    },

    [Actions.GET_CHECKINS_SUCCESS]: (state: State, action: ReturnType<typeof getCheckInsSuccess>) => {
        const checkIns = action.payload;
        const { deviceId } = action.meta;
        const { data, loading, errors } = state;
        const oldCheckIns = data[deviceId];

        // Only create a new array if we're actually prepending something
        const newCheckIns =
            checkIns?.length
                ? [ ...checkIns, ...(oldCheckIns ? oldCheckIns.slice(0, MAX_CHECKINS - checkIns.length) : []) ]
                : oldCheckIns;

        return {
            ...state,
            data: {
                ...data,
                [deviceId]: newCheckIns,
            },
            loading: {
                ...loading,
                [deviceId]: false,
            },
            errors: omit(errors, deviceId),
        };
    },

    [Actions.GET_CHECKINS_FAILURE]: (state: State, action: ReturnType<typeof getCheckInsFailure>) => {
        const { deviceId } = action.meta;
        return {
            ...state,
            loading: {
                ...state.loading,
                [deviceId]: false,
            },
            errors: {
                ...state.errors,
                [deviceId]: action.payload,
            },
        };
    },


    //
    // Device Actions
    //
    [DeviceActions.DELETE_DEVICE_SUCCESS]: (state: State, action: ReturnType<typeof deleteDeviceSuccess>) => {
        const { deviceId } = action.meta;
        return {
            data: omit(state.data, deviceId),
            errors: omit(state.errors, deviceId),
            loading: omit(state.loading, deviceId),
        };
    },
    

    //
    //  Session Actions
    //
    [SessionActions.LOGOUT_SUCCESS]: (state: State, action: ReturnType<typeof logoutSuccess>) => {
        return initialState;
    },
} as any, initialState);
